<template>
  <div class="partialReturnApprovalModal" align-center justify-center ma-4>
    <NsModal
      :fullscreen="false"
      :info="true"
      :persistent="true"
      v-model="show"
    >
      <div class="partialReturnApprovalModalContent">
        <div class="partialReturnModalHeader">
          <div class="partialReturnModalHeaderText">{{header}}</div>
          <div class="partialReturnModalHeaderClose" @click="closeModal()">
            <NsIcon icon-name="icon-close" />
          </div>
        </div>
        <p class="partialReturnModalText">{{ text }}</p>
        <div class="partialReturnModalButtons">
          <button
            class="partialReturnModalCancelButton"
            @click="closeModal()"
          >
            {{cancelText}}
          </button>
          <button
            v-if="showReturnAll"
            class="partialReturnModalAcceptButton partialReturnModalReturnAllButton"
            :class="{ disableReturnModalButton: returningAllocations === true }"
            @click="closeModal({ returnItems: 'all' })"
          >
            <CustomSpinner v-if="returningAllocations" /> {{returnAllText}}
          </button>
          <button
            v-else
            class="partialReturnModalAcceptButton partialReturnModalReturnSelectedButton"
            :class="{ disableReturnModalButton: returningAllocations === true }"
            @click="closeModal({ returnItems: 'selected' })"
          >
            <CustomSpinner v-if="returningAllocations" />{{returnSomeText}} ( {{returnAmount}} )
          </button>
        </div>
      </div>
    </NsModal>
  </div>
</template>

<script>
import { events } from '@nuskin/ns-util'
import {
  NsModal,
} from '@nuskin/design-components'
import {
  NsIcon,
} from '@nuskin/ns-icon'

import QualificationService from '@nuskin/exclusive-offer-sdk'

import CustomSpinner from '../../components/CustomSpinner.vue'
import config from '../../js/qualificationsConfigService'
export default {
  components: {
    NsModal,
    NsIcon,
    CustomSpinner,
  },
  data() {
    return {
      returningAllocations: false,
      returnError: false,
      tdc: config.getTdc(),
    }
  },
  methods: {
    async closeModal(options = { returnItems: 'none' }) {
      if (this.returningAllocations === false) {
        const { returnItems } = options
        if (returnItems === 'all') {
          await this.returnAllQualifications()
          if (!this.returnError) {
            this.$emit('close-return-modal', options)
          }
        } else if (returnItems === 'selected') {
          await this.returnSelectedQualifications()
          if (!this.returnError) {
            this.$emit('close-return-modal', options)
          }
        } else {
          this.returnError = false
          this.show = false
          this.$emit('close-return-modal')
        }
      }
    },
    async returnAllQualifications() {
      this.returningAllocations = true
      try {
        await Promise.all(
          Object.keys(this.qualifications)
            .filter((sku) => this.qualifications[sku].shareBackList.length > 0)
            .map((sku) => {
              return QualificationService.returnQualifications(sku)
            }),
        )
        QualificationService.getQualifications()
        this.returnError = false
        events.publish(events.shop.QUALIFICATION_STATUS_CHANGED)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
        this.returnError = true
      } finally {
        this.returningAllocations = false
      }
    },
    async returnSelectedQualifications() {
      if (this.returnAmount > 0 && this.returnList) {
        this.returningAllocations = true
        try {
          const returnPromises = Object.keys(this.returnList).map((sku) => {
            const returnArray = []
            for (const [key, value] of Object.entries(this.returnList[sku])) {
              returnArray.push({
                customerId: key,
                quantity: value,
              })
            }

            return QualificationService.returnPartialQualifications(sku, returnArray)
          })

          await Promise.all(returnPromises)
          this.returnError = false
          QualificationService.getQualifications()
          events.publish(events.shop.QUALIFICATION_STATUS_CHANGED)
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e)
          this.returnError = true
        } finally {
          this.returningAllocations = false
        }
      } else {
        // eslint-disable-next-line no-console
        console.error('ReturnAmount or ReturnList not set properly', this.returnAmount, this.returnList)
      }
    },
  },
  props: {
    cancelText: {
      type: String,
      default: 'Cancel',
    },
    header: {
      type: String,
      default: 'Are you sure you want to return these items?',
    },
    qualifications: {
      type: Object,
      default: () => {},
    },
    returnAllText: {
      type: String,
      default: 'Return All Allocations',
    },
    returnSomeText: {
      type: String,
      default: 'Return Items',
    },
    returnAmount: {
      type: Number,
      default: 0,
    },
    returnList: {
      type: Object,
      default: () => {},
    },
    show: Boolean,
    showReturnAll: Boolean,
    text: {
      type: String,
      default: 'They will be returned to the person who transferred them to you.',
    },
  },
}
</script>

<style lang="scss">
  @use "sass:map";
  @use "~@nuskin/ns-core-styles/src/scss/colors";

  $noticeColor: map.get(colors.$baseColors, "notice-color");
  $blackhole: map.get(colors.$baseColors, "blackhole");
  .partialReturnApprovalModalContent {
    display: flex;
    flex-direction: column;
    color: $blackhole;
    font-family: 'Proxima Nova', sans-serif;
    .partialReturnModalHeader {
      display: flex;
      justify-content: space-between;
      margin-bottom: 15px;
      .partialReturnModalHeaderText {
        font-weight: 600;
        font-size: 18px;
        text-align: left;
      }
      .partialReturnModalHeaderClose {
        cursor: pointer;
      }
    }
    .partialReturnModalText {
      align-self: flex-start;
      margin-bottom: 15px;
      font-weight: 300;
      color: $blackhole;
      text-align: left;
    }
    .partialReturnModalButtons {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 15px;
      button {
        border-radius: 3px;
        align-items: center;
        font-size: 16px;
        font-weight: 400;
        height: 45px;
        width: 345px;
        display: flex;
        justify-content: center;
        margin-bottom: 0;
      }
      button.partialReturnModalCancelButton {
        font-weight: 600;
        padding-left: 50px;
      }
      button.partialReturnModalAcceptButton {
        background-color: $blackhole;
        color: white;
        font-weight: 400;
      }
      button.disableReturnModalButton {
        background-color: #515157;
      }
      button.partialReturnModalAcceptButton:hover {
        background-color: #515157;
        border-color: #515157;
      }
    }
    .returnError {
      color: #B2140A;
      font-size: 16px;
    }
  }

  // Overriding NsModal's default behavior.
  .v-dialog__container {
    .v-dialog.modal-info {

      // We can't access the close button's and change its behavior.
      // So we don't display it.
      .modal__header {
        display: none;
      }
      .v-card__text {
        margin-top: 0;
        .container.grid-list-md {
          padding-bottom: 0;
        }
      }
    }
  }

  @media (max-width: 375px) {
    .partialReturnModalButtons {
      flex-direction: column-reverse;
      button {
        width: auto !important;
      }

      button.partialReturnModalCancelButton {
        margin-top: 10px;
        padding-left: 0 !important;
        border: 1px solid #272525;
      }
    }
  }
</style>
